import { Col, Row } from '@opendoor/atoms/box';
import { Combobox } from '@opendoor/atoms/combobox';
import { Field } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';
import type { ErrorKey, RangeOption } from 'components/marketplace/search/types';

import { buildRangeFilterOptions, filterIntOptions } from './utils';

const MIN_PLACEHOLDER: RangeOption = { label: 'No min', value: '0' };
const MAX_PLACEHOLDER: RangeOption = { label: 'No max', value: '500000000' };

const YearBuiltOptions: RangeOption[] = [
  { label: '1900', value: '1900' },
  { label: '1920', value: '1920' },
  { label: '1940', value: '1940' },
  { label: '1960', value: '1960' },
  { label: '1980', value: '1980' },
  { label: '2000', value: '2000' },
  { label: '2005', value: '2005' },
  { label: '2010', value: '2010' },
  { label: '2015', value: '2015' },
  { label: '2016', value: '2016' },
  { label: '2017', value: '2017' },
  { label: '2018', value: '2018' },
  { label: '2019', value: '2019' },
  { label: '2020', value: '2020' },
  { label: '2021', value: '2021' },
  { label: '2022', value: '2022' },
  { label: '2023', value: '2023' },
  { label: '2024', value: '2024' },
];

export interface YearBuiltFilterProps {
  minYearBuilt?: string | null;
  maxYearBuilt?: string | null;
  handleMinYearBuiltChange: (value: string) => void;
  handleMaxYearBuiltChange: (value: string) => void;
  handleError: (key: ErrorKey, val: string) => void; // used to notify parent component of error state
  errors?: Record<ErrorKey, string> | null;
}

export const YearBuiltFilter = ({
  minYearBuilt,
  maxYearBuilt,
  handleMinYearBuiltChange,
  handleMaxYearBuiltChange,
  handleError,
  errors,
}: YearBuiltFilterProps) => {
  const onMinYearBuildChange = (value: string) => {
    if (maxYearBuilt && parseInt(value, 10) > parseInt(maxYearBuilt, 10)) {
      handleError('minYearBuilt', 'Min year built must be less than max year built');
      return;
    }
    handleError('minYearBuilt', '');
    handleMinYearBuiltChange(value);
  };

  const onMaxPriceChange = (value: string) => {
    if (minYearBuilt && parseInt(value, 10) < parseInt(minYearBuilt, 10)) {
      handleError('maxYearBuilt', 'Max year built must be greater than min year built');
      return;
    }
    handleError('maxYearBuilt', '');
    handleMaxYearBuiltChange(value);
  };

  const minOptions = buildRangeFilterOptions(
    filterIntOptions(YearBuiltOptions, 'lt', maxYearBuilt ?? undefined),
    MIN_PLACEHOLDER,
  );
  const maxOptions = buildRangeFilterOptions(
    filterIntOptions(YearBuiltOptions, 'gt', minYearBuilt ?? undefined),
    MAX_PLACEHOLDER,
  );

  return (
    <Col className="w-full gap-8">
      <Text as="p" className="text-label-xl">
        Year built
      </Text>
      <Row className="w-full justify-between">
        <Field className="w-[48%]" error={errors?.['minYearBuilt'] || undefined}>
          <Combobox
            aria-label="Minimum year built"
            placeholder={MIN_PLACEHOLDER.label}
            options={minOptions}
            value={minOptions.find((option) => option.value === minYearBuilt) ?? null}
            error={Boolean(errors?.['minYearBuilt'])}
            size="sm"
            onValueChange={(option) => onMinYearBuildChange(option?.value ?? MIN_PLACEHOLDER.value)}
          />
        </Field>
        <Field className="w-[48%]" error={errors?.['maxYearBuilt'] || undefined}>
          <Combobox
            aria-label="Maximum year built"
            placeholder={MAX_PLACEHOLDER.label}
            options={maxOptions}
            value={maxOptions.find((option) => option.value === maxYearBuilt) ?? null}
            error={Boolean(errors?.['maxYearBuilt'])}
            size="sm"
            onValueChange={(option) => onMaxPriceChange(option?.value ?? MAX_PLACEHOLDER.value)}
          />
        </Field>
      </Row>
    </Col>
  );
};
